import React from 'react';
import clsx from 'clsx';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/common/Tooltip';

interface EventUsageBarProps {
  usedCount: number;
  totalCount: number;
  eventNames: string[];
  isGlobalTransform?: boolean; // New prop to indicate if it's a global transform
}

const EventUsageBar: React.FC<EventUsageBarProps> = ({ usedCount, totalCount, eventNames, isGlobalTransform }) => {
  const percentage = totalCount === 0 ? 0 : (usedCount / totalCount) * 100;

  let barColorClass = 'bg-blue-500';

  // If it's a global transform, always highlight all bars
  if (isGlobalTransform) {
    barColorClass = 'bg-blue-500'; // Or a distinct color for global transforms if desired
  } else if (percentage > 0) {
    barColorClass = 'bg-blue-500';
  } else {
    barColorClass = 'bg-gray-300 dark:bg-gray-600';
  }

  const tooltipText = isGlobalTransform ? "Global Transform: Applied to all events" : 
                       totalCount === 0 ? "No events configured" : 
                       `${usedCount} / ${totalCount} events`;

  return (
    <Tooltip>
      <TooltipTrigger>
        <div className="flex items-center text-sm">
          <div className="flex w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" style={{ gap: '1px' }}>
            {totalCount > 0 ? (
              Array.from({ length: totalCount }).map((_, index) => (
                <div
                  key={`bar-segment-${index}`}
                  className={clsx(
                    "h-full flex-1",
                    isGlobalTransform || index < usedCount ? barColorClass : 'bg-gray-300 dark:bg-gray-600'
                  )}
                />
              ))
            ) : (
              // Render a single empty bar segment if totalCount is 0
              <div className="h-full flex-1 bg-gray-300 dark:bg-gray-600" />
            )}
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <div className="font-semibold mb-1">{tooltipText}</div>
        {isGlobalTransform ? (
          <div>This transform applies to all events.</div>
        ) : eventNames.length > 0 ? (
          <ul className="list-disc list-inside">
            {eventNames.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
        ) : (
          <div>No events assigned.</div>
        )}
      </TooltipContent>
    </Tooltip>
  );
};

export default EventUsageBar;
